import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Legend } from 'recharts';

const VulnerabilityChart = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [chartData, setChartData] = useState([]);
  const [newMonthName, setNewMonthName] = useState('');
  const [newYear, setNewYear] = useState(new Date().getFullYear());
  const [newScore, setNewScore] = useState('');
  const [editIndex, setEditIndex] = useState(null);
  // New state for date filtering
  const [filterStartYear, setFilterStartYear] = useState('');
  const [filterEndYear, setFilterEndYear] = useState('');
  const [dateFilterActive, setDateFilterActive] = useState(false);

  // Array of month names for dropdown
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Get available years from chart data
  const availableYears = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    
    const years = new Set();
    chartData.forEach(point => {
      const year = point.month.split(' ')[1];
      if (year) years.add(year);
    });
    
    return Array.from(years).sort();
  }, [chartData]);

  // Initialize filter with earliest and latest years when data changes
  useEffect(() => {
    if (availableYears.length > 0 && !filterStartYear && !filterEndYear) {
      setFilterStartYear(availableYears[0]);
      setFilterEndYear(availableYears[availableYears.length - 1]);
    }
  }, [availableYears, filterStartYear, filterEndYear]);

  // Apply date filter to chart data
  const filteredChartData = useMemo(() => {
    if (!dateFilterActive || !chartData || chartData.length === 0) return chartData;
    
    return chartData.filter(point => {
      const pointYear = parseInt(point.month.split(' ')[1]);
      const start = filterStartYear ? parseInt(filterStartYear) : 0;
      const end = filterEndYear ? parseInt(filterEndYear) : 9999;
      
      return pointYear >= start && pointYear <= end;
    });
  }, [chartData, dateFilterActive, filterStartYear, filterEndYear]);

  // Load clients and their data from localStorage on component mount
  useEffect(() => {
    const savedClients = localStorage.getItem('vulnerabilityClients');
    if (savedClients) {
      setClients(JSON.parse(savedClients));
    }
  }, []);

  // Load client data when a client is selected
  useEffect(() => {
    if (selectedClient) {
      const clientData = localStorage.getItem(`vulnerabilityData_${selectedClient}`);
      if (clientData) {
        // Sort data by date when loading
        const parsedData = JSON.parse(clientData);
        const sortedData = sortDataByDate(parsedData);
        setChartData(sortedData);
      } else {
        setChartData([]);
      }
    }
  }, [selectedClient]);

  // Function to sort data points by date
  const sortDataByDate = (data) => {
    return [...data].sort((a, b) => {
      const [aMonth, aYear] = a.month.split(' ');
      const [bMonth, bYear] = b.month.split(' ');
      
      // Compare years first
      if (aYear !== bYear) {
        return parseInt(aYear) - parseInt(bYear);
      }
      
      // If years are the same, compare months
      return months.indexOf(aMonth) - months.indexOf(bMonth);
    });
  };

  const addClient = () => {
    if (newClientName && !clients.includes(newClientName)) {
      const updatedClients = [...clients, newClientName];
      setClients(updatedClients);
      localStorage.setItem('vulnerabilityClients', JSON.stringify(updatedClients));
      setNewClientName('');
      setSelectedClient(newClientName);
    }
  };

  const saveData = () => {
    if (selectedClient) {
      localStorage.setItem(`vulnerabilityData_${selectedClient}`, JSON.stringify(chartData));
      alert('Data saved successfully!');
    }
  };

  const exportData = () => {
    if (selectedClient) {
      const exportData = {
        client: selectedClient,
        data: chartData
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vulnerability_data_${selectedClient}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const importData = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result);
          if (importedData.client && importedData.data) {
            // Add client if it doesn't exist
            if (!clients.includes(importedData.client)) {
              const updatedClients = [...clients, importedData.client];
              setClients(updatedClients);
              localStorage.setItem('vulnerabilityClients', JSON.stringify(updatedClients));
            }
            // Sort the data before saving
            const sortedData = sortDataByDate(importedData.data);
            // Save data and update current view
            localStorage.setItem(`vulnerabilityData_${importedData.client}`, JSON.stringify(sortedData));
            setSelectedClient(importedData.client);
            setChartData(sortedData);
            alert('Data imported successfully!');
          }
        } catch (error) {
          alert('Error importing data: Invalid file format');
        }
      };
      reader.readAsText(file);
    }
    event.target.value = '';
  };

  const addDataPoint = () => {
    if (newMonthName && newYear && newScore) {
      const formattedMonth = `${newMonthName} ${newYear}`;
      
      // Check if this month/year combination already exists
      const existingIndex = chartData.findIndex(
        point => point.month === formattedMonth
      );
      
      if (existingIndex >= 0) {
        if (!window.confirm(`A data point for ${formattedMonth} already exists. Do you want to update it?`)) {
          return;
        }
        // Update existing data point instead of adding a new one
        const updatedData = [...chartData];
        updatedData[existingIndex].score = parseFloat(newScore);
        setChartData(sortDataByDate(updatedData));
      } else {
        // Add new data point and sort
        const newData = [...chartData, { 
          month: formattedMonth, 
          score: parseFloat(newScore)
        }];
        setChartData(sortDataByDate(newData));
      }
      
      setNewMonthName('');
      setNewScore('');
    }
  };

  const removeDataPoint = (index) => {
    setChartData(chartData.filter((_, i) => i !== index));
  };

  const updateDataPoint = (index) => {
    const formattedMonth = `${newMonthName} ${newYear}`;
    const updatedData = [...chartData];
    updatedData[index] = { 
      month: formattedMonth, 
      score: parseFloat(newScore) 
    };
    setChartData(sortDataByDate(updatedData));
    setEditIndex(null);
    setNewMonthName('');
    setNewScore('');
  };

  const startEdit = (index) => {
    setEditIndex(index);
    const [month, year] = chartData[index].month.split(' ');
    setNewMonthName(month);
    setNewYear(parseInt(year));
    setNewScore(chartData[index].score.toString());
  };

  // Toggle date filter
  const toggleDateFilter = () => {
    setDateFilterActive(!dateFilterActive);
  };

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
          <p className="font-semibold">{label}</p>
          <p className="text-sm">
            Score: <span className="font-semibold">{payload[0].value.toFixed(2)}</span>
          </p>
          <p className="text-xs mt-1">
            {getScoreLabel(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  // Helper to get score label based on value
  const getScoreLabel = (score) => {
    if (score <= 40) return "Green (OK)";
    if (score <= 45) return "Light Green (Still OK)";
    if (score <= 60) return "Orange (Getting Worse)";
    if (score <= 75) return "Dark Orange (Not So Good)";
    if (score <= 90) return "Light Red (Very Bad)";
    return "Red (Critical)";
  };

  return (
    <div className="w-full bg-white p-4">
      {/* Client Management Section */}
      <div className="mb-6 bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">Client Management</h3>
        <div className="flex gap-4 mb-4">
          <input
            type="text"
            value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)}
            placeholder="New Client Name"
            className="flex-1 p-2 border rounded"
          />
          <button
            onClick={addClient}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            Add Client
          </button>
        </div>
        <div className="flex gap-4 mb-4">
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="flex-1 p-2 border rounded"
          >
            <option value="">Select Client</option>
            {clients.map((client) => (
              <option key={client} value={client}>
                {client}
              </option>
            ))}
          </select>
          <button
            onClick={saveData}
            disabled={!selectedClient}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-400"
          >
            Save
          </button>
          <button
            onClick={exportData}
            disabled={!selectedClient}
            className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 disabled:bg-gray-400"
          >
            Export
          </button>
          <label className="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 cursor-pointer">
            Import
            <input
              type="file"
              accept=".json"
              className="hidden"
              onChange={importData}
            />
          </label>
        </div>
      </div>

      {selectedClient ? (
        <>
          {/* Data Management Section */}
          <div className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Manage Data Points for {selectedClient}</h3>
            <div className="flex gap-4 mb-4">
              {/* Month Selection */}
              <select
                value={newMonthName}
                onChange={(e) => setNewMonthName(e.target.value)}
                className="flex-1 p-2 border rounded"
              >
                <option value="">Select Month</option>
                {months.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>

              {/* Year Input */}
              <input
                type="number"
                value={newYear}
                onChange={(e) => setNewYear(parseInt(e.target.value))}
                placeholder="Year"
                min="2000"
                max="2100"
                className="w-32 p-2 border rounded"
              />

              {/* Score Input */}
              <input
                type="number"
                value={newScore}
                onChange={(e) => setNewScore(e.target.value)}
                placeholder="Score"
                step="0.01"
                min="0"
                max="100"
                className="w-32 p-2 border rounded"
              />

              {/* Add/Update Button */}
              {editIndex !== null ? (
                <button
                  onClick={() => updateDataPoint(editIndex)}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                >
                  Update
                </button>
              ) : (
                <button
                  onClick={addDataPoint}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  disabled={!newMonthName || !newScore}
                >
                  Add
                </button>
              )}
            </div>

            {/* Date Filter Controls */}
            {chartData.length > 0 && (
              <div className="mt-6 mb-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-md font-medium">Date Range Filter</h4>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="date-filter-toggle"
                      checked={dateFilterActive}
                      onChange={toggleDateFilter}
                      className="mr-2 h-4 w-4"
                    />
                    <label htmlFor="date-filter-toggle" className="text-sm">
                      {dateFilterActive ? "Filter Active" : "Filter Inactive"}
                    </label>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm mb-1">Start Year</label>
                    <select
                      value={filterStartYear}
                      onChange={(e) => setFilterStartYear(e.target.value)}
                      className="w-full p-2 border rounded"
                      disabled={!dateFilterActive}
                    >
                      <option value="">Any</option>
                      {availableYears.map(year => (
                        <option key={`start-${year}`} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm mb-1">End Year</label>
                    <select
                      value={filterEndYear}
                      onChange={(e) => setFilterEndYear(e.target.value)}
                      className="w-full p-2 border rounded"
                      disabled={!dateFilterActive}
                    >
                      <option value="">Any</option>
                      {availableYears.map(year => (
                        <option key={`end-${year}`} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Data Table */}
            <div className="overflow-x-auto mt-4 max-h-64 overflow-y-auto border border-gray-200 rounded">
              <table className="min-w-full bg-white">
                <thead className="sticky top-0 bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 border">Month/Year</th>
                    <th className="px-4 py-2 border">Score</th>
                    <th className="px-4 py-2 border">Status</th>
                    <th className="px-4 py-2 border">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredChartData.map((point, index) => {
                    const pointIndex = chartData.findIndex(p => p.month === point.month);
                    return (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="px-4 py-2 border">{point.month}</td>
                        <td className="px-4 py-2 border">{point.score.toFixed(2)}</td>
                        <td className={`px-4 py-2 border ${
                          point.score <= 40 ? 'text-green-600' :
                          point.score <= 45 ? 'text-green-500' :
                          point.score <= 60 ? 'text-orange-500' :
                          point.score <= 75 ? 'text-orange-600' :
                          point.score <= 90 ? 'text-red-500' : 'text-red-600'
                        }`}>
                          {getScoreLabel(point.score)}
                        </td>
                        <td className="px-4 py-2 border">
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() => startEdit(pointIndex)}
                              className="text-blue-500 hover:text-blue-700"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => removeDataPoint(pointIndex)}
                              className="text-red-500 hover:text-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart Section */}
          <div className="h-96">
            <h2 className="text-lg font-bold mb-4 text-center">
              {selectedClient} - ASI Vulnerability Management Scores 
              {dateFilterActive && filterStartYear && filterEndYear ? 
                ` (${filterStartYear} - ${filterEndYear})` : 
                ' (All Time)'}
            </h2>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={filteredChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  label={{ 
                    value: 'Months', 
                    position: 'bottom',
                    offset: 0
                  }}
                  tick={{ fontSize: 12 }}
                  angle={filteredChartData.length > 12 ? -45 : 0}
                  textAnchor={filteredChartData.length > 12 ? "end" : "middle"}
                  height={filteredChartData.length > 12 ? 70 : 30}
                />
                <YAxis 
                  domain={[0, 100]} 
                  label={{ 
                    value: 'Score', 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: 10
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                
                <ReferenceLine y={40} stroke="#4CAF50" strokeDasharray="3 3" />
                <ReferenceLine y={45} stroke="#8BC34A" strokeDasharray="3 3" />
                <ReferenceLine y={60} stroke="#FF9800" strokeDasharray="3 3" />
                <ReferenceLine y={75} stroke="#F57C00" strokeDasharray="3 3" />
                <ReferenceLine y={90} stroke="#EF5350" strokeDasharray="3 3" />
                
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#000" 
                  strokeWidth={2}
                  dot={{
                    fill: '#000',
                    stroke: '#000',
                    strokeWidth: 2,
                    r: 4,
                  }}
                  activeDot={{
                    r: 6,
                    fill: '#2563eb',
                    stroke: '#fff',
                    strokeWidth: 2
                  }}
                  name="ASI Vulnerability Score"
                />
                
                <Legend 
                  verticalAlign="bottom" 
                  height={120}
                  content={({ payload }) => (
                    <div className="text-xs mt-8">
                      <div className="flex items-center mb-1">
                        <div className="w-6 h-0 border-t-2 border-black mr-2"></div>
                        <span>ASI Vulnerability Score</span>
                      </div>
                      <div className="flex items-center mb-1">
                        <div className="w-6 h-0 border-t-2 border-dashed border-green-500 mr-2"></div>
                        <span>40 - Green (OK)</span>
                      </div>
                      <div className="flex items-center mb-1">
                        <div className="w-6 h-0 border-t-2 border-dashed border-light-green-500 mr-2"></div>
                        <span>45 - Light Green (Still OK)</span>
                      </div>
                      <div className="flex items-center mb-1">
                        <div className="w-6 h-0 border-t-2 border-dashed border-orange-500 mr-2"></div>
                        <span>60 - Orange (Getting Worse)</span>
                      </div>
                      <div className="flex items-center mb-1">
                        <div className="w-6 h-0 border-t-2 border-dashed border-orange-700 mr-2"></div>
                        <span>75 - Dark Orange (Not So Good)</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-6 h-0 border-t-2 border-dashed border-red-500 mr-2"></div>
                        <span>90 - Light Red (Very Bad)</span>
                      </div>
                    </div>
                  )}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <div className="text-center text-gray-600 py-8">
          Please select or add a client to view and manage vulnerability data.
        </div>
      )}
    </div>
  );
};

export default VulnerabilityChart; 